// Who paid an expense — or that nobody has yet.
//
// The payer column answers "whose money went out", so an unpaid expense has nothing to
// put there. It shows the state instead of an empty cell: the cost is real and counted
// in the job's expenses and profit, it simply has not moved any partner's money yet and
// so stays out of the partner balance until it is marked paid.
//
// Used by both expense screens (a job's panel and the office list), which is why it
// lives here rather than in either one.
import he from '../i18n/he';
import { isUnpaid, isAwaitingReimbursement, wasReimbursed } from '../utils/expenses';
import { payersLine } from '../utils/payers';

export default function PayerCell({ expense }) {
  // A FRONTED COST IS NOT SIMPLY "UNPAID", and saying so would lose the only fact that
  // distinguishes it: somebody's own money is currently in it. The plain unpaid badge
  // reads as "nothing has happened yet", which for the secretary who bought the cakes is
  // the opposite of the truth. Checked FIRST, because a fronted cost is also unpaid and
  // the more specific state is the one worth showing.
  //
  // It says who, because "to be reimbursed" with no name is a debt to nobody. Once a
  // partner pays them back the expense is marked paid and this falls through to the
  // payer's name below — the record of who actually went to the shop stays on the row.
  if (isAwaitingReimbursement(expense)) {
    return (
      <span className="badge amber">
        {he.expenses.frontedByLine(expense.incurred_by_user_name || '—')}
      </span>
    );
  }
  if (isUnpaid(expense)) return <span className="badge amber">{he.expenses.unpaid}</span>;

  // PAID, BUT SOMEBODY ELSE HAD PUT THE MONEY IN FIRST. Both names, because the row is
  // the record of two facts and the payer alone is only one of them: a partner's money
  // left (which is what the gap reads) AND that partner was paying somebody back. Read
  // mostly in the paid archive, months later, when nobody remembers whose errand it was.
  //
  // A SPLIT reads each partner with their part (069) — "דנה 600 · יוסי 400" — because
  // which partner paid how much is exactly what the gap credits.
  if (wasReimbursed(expense)) {
    return <>{he.expenses.reimbursedLine(payersLine(expense.payers), expense.incurred_by_user_name || '—')}</>;
  }
  return <>{payersLine(expense.payers)}</>;
}
